(() => {
  const root = document.querySelector('[data-product-root]');
  const configNode = root?.querySelector('[data-product-config]');
  if (!root || !configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    console.error('Product configuration could not be read.', error);
    return;
  }

  const form = root.querySelector('[data-product-form]');
  const variantInput = form.querySelector('[data-variant-id]');
  const sellingPlanInput = form.querySelector('[data-selling-plan-input]');
  const planGroupsRoot = form.querySelector('[data-selling-plan-groups]');
  const quantityInput = form.querySelector('[data-quantity]');
  const addButton = form.querySelector('[data-add-to-cart]');
  const state = {
    options: [...root.querySelectorAll('[data-option-position].is-selected')].map((button) => button.dataset.optionValue),
    variant: null,
    purchaseGroup: null,
    sellingPlanId: null
  };

  const formatMoney = (cents) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: config.currency || 'USD'
  }).format((Number(cents) || 0) / 100);
  const getVariant = () => config.variants.find((variant) => variant.options.every((value, index) => value === state.options[index])) || config.variants[0];
  const getPlan = () => state.variant?.plans.find((plan) => String(plan.id) === String(state.sellingPlanId));
  const getPreferredPlan = (plans) => {
    if (!plans.length) return null;
    const primaryGroupPlans = plans.filter((plan) => String(plan.groupId) === String(plans[0].groupId));
    return primaryGroupPlans.find((plan) => /delivered every month$/i.test(plan.name))
      || primaryGroupPlans.find((plan) => /every month/i.test(plan.name) && !/\b\d+\s+months?\b/i.test(plan.name))
      || primaryGroupPlans[0];
  };

  function setGalleryMedia(mediaId) {
    if (!mediaId) return;
    const panel = root.querySelector(`[data-media-panel="${mediaId}"]`) || root.querySelector('[data-media-default]');
    if (!panel) return;
    root.querySelectorAll('[data-media-panel]').forEach((node) => {
      node.hidden = node !== panel;
      if (node !== panel) node.querySelectorAll('video').forEach((video) => video.pause());
    });
    root.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(mediaId))));
    const stage = root.querySelector('.product-stage');
    if (stage) stage.dataset.mediaKind = panel.dataset.mediaKind || 'product';
  }

  function chooseDefaultPurchase() {
    const plans = state.variant?.plans || [];
    const previousPlan = plans.find((plan) => String(plan.id) === String(state.sellingPlanId));
    if (previousPlan) {
      state.purchaseGroup = String(previousPlan.groupId);
      return;
    }
    if ((config.defaultSubscription || config.requiresSellingPlan) && plans.length) {
      const preferredPlan = getPreferredPlan(plans);
      state.purchaseGroup = String(preferredPlan.groupId);
      state.sellingPlanId = String(preferredPlan.id);
      return;
    }
    state.purchaseGroup = 'one-time';
    state.sellingPlanId = null;
  }

  function renderSellingPlans() {
    planGroupsRoot.replaceChildren();
    const groups = new Map();
    (state.variant.plans || []).forEach((plan) => {
      const groupId = String(plan.groupId);
      if (!groups.has(groupId)) groups.set(groupId, { id: groupId, name: plan.groupName, plans: [] });
      groups.get(groupId).plans.push(plan);
    });

    groups.forEach((group) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'purchase-option';
      wrapper.dataset.purchaseOption = group.id;
      const card = document.createElement('label');
      card.className = 'purchase-card';
      const groupRadio = document.createElement('input');
      groupRadio.type = 'radio';
      groupRadio.name = root.dataset.purchaseRadioName;
      groupRadio.value = group.id;
      groupRadio.dataset.purchaseGroup = group.id;
      groupRadio.checked = state.purchaseGroup === group.id;
      groupRadio.addEventListener('change', () => {
        state.purchaseGroup = group.id;
        const current = group.plans.find((plan) => String(plan.id) === String(state.sellingPlanId));
        state.sellingPlanId = String((current || getPreferredPlan(group.plans)).id);
        updatePurchaseUI();
      });

      const mark = document.createElement('span');
      mark.className = 'radio-mark';
      mark.setAttribute('aria-hidden', 'true');
      const copy = document.createElement('span');
      copy.className = 'purchase-copy';
      const name = document.createElement('strong');
      name.textContent = group.name || 'Subscribe and save';
      const note = document.createElement('small');
      note.textContent = 'Skip, swap, pause, or cancel when the watch changes.';
      copy.append(name, note);
      const price = document.createElement('strong');
      price.className = 'purchase-price';
      // Show the price of the plan actually selected, not the cheapest one in
      // the group. Advertising the minimum quoted the every-2-weeks tier while
      // the monthly plan was the one selected and charged.
      price.dataset.groupPrice = group.id;
      const shownPlan = group.plans.find((plan) => String(plan.id) === String(state.sellingPlanId))
        || getPreferredPlan(group.plans);
      price.textContent = `${formatMoney(shownPlan.perDeliveryPrice || shownPlan.price)} each`;
      card.append(groupRadio, mark, copy, price);

      const choices = document.createElement('div');
      choices.className = 'frequency-panel';
      choices.setAttribute('aria-label', `${group.name} delivery frequency`);
      group.plans.forEach((plan) => {
        const label = document.createElement('label');
        label.className = 'frequency-button';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `selling_plan_${group.id}`;
        input.value = plan.id;
        input.checked = state.purchaseGroup === group.id && String(plan.id) === String(state.sellingPlanId);
        input.addEventListener('change', () => {
          state.purchaseGroup = group.id;
          state.sellingPlanId = String(plan.id);
          updatePurchaseUI();
        });
        const main = document.createElement('span');
        main.className = 'frequency-main';
        main.textContent = plan.name;
        const save = document.createElement('span');
        save.className = 'frequency-save';
        const savings = Math.max(0, (plan.compareAtPrice || state.variant.price) - plan.price);
        save.textContent = savings ? `Save ${formatMoney(savings)}` : formatMoney(plan.perDeliveryPrice || plan.price);
        label.append(input, main, save);
        choices.append(label);
      });
      wrapper.append(card, choices);
      planGroupsRoot.append(wrapper);
    });
  }

  function updatePurchaseUI() {
    const plan = getPlan();
    const usingPlan = state.purchaseGroup !== 'one-time' && plan;
    const unitPrice = usingPlan ? plan.price : state.variant.price;
    const quantity = Math.max(1, Number(quantityInput.value) || 1);

    root.querySelectorAll('[data-purchase-card]').forEach((card) => card.classList.toggle('is-selected', card.dataset.purchaseCard === state.purchaseGroup));
    const oneTimeRadio = root.querySelector('[data-purchase-option="one-time"]');
    if (oneTimeRadio) oneTimeRadio.checked = state.purchaseGroup === 'one-time';
    root.querySelectorAll('[data-purchase-group]').forEach((radio) => {
      radio.checked = radio.value === state.purchaseGroup;
      radio.closest('.purchase-option')?.classList.toggle('is-active', radio.checked);
    });
    root.querySelectorAll('.frequency-button input').forEach((radio) => {
      radio.checked = String(radio.value) === String(state.sellingPlanId) && state.purchaseGroup !== 'one-time';
      radio.closest('.frequency-button')?.classList.toggle('is-selected', radio.checked);
    });

    // Keep the group card price in step with the chosen frequency.
    if (usingPlan) {
      const groupPriceNode = root.querySelector(`[data-group-price="${state.purchaseGroup}"]`);
      if (groupPriceNode) groupPriceNode.textContent = `${formatMoney(plan.perDeliveryPrice || plan.price)} each`;
    }

    sellingPlanInput.disabled = !usingPlan;
    sellingPlanInput.value = usingPlan ? plan.id : '';
    root.querySelector('[data-purchase-label]').textContent = usingPlan ? plan.name : 'One-time purchase';
    root.querySelector('[data-summary-delivery]').textContent = usingPlan ? plan.name : 'One time';
    root.querySelector('[data-summary-total]').textContent = formatMoney(unitPrice * quantity);
    root.querySelector('[data-add-price]').textContent = formatMoney(unitPrice * quantity);
    root.querySelector('[data-product-price]').textContent = formatMoney(unitPrice);

    const compareNode = root.querySelector('[data-product-compare-price]');
    const comparePrice = usingPlan ? plan.compareAtPrice : state.variant.compareAtPrice;
    if (compareNode) {
      compareNode.hidden = !comparePrice || comparePrice <= unitPrice;
      compareNode.textContent = comparePrice ? formatMoney(comparePrice) : '';
    }
    const savings = comparePrice && comparePrice > unitPrice ? comparePrice - unitPrice : 0;
    root.querySelector('[data-savings-label]').textContent = savings ? `Save ${formatMoney(savings)} per delivery` : '';
  }

  function updateVariant() {
    state.variant = getVariant();
    variantInput.value = state.variant.id;
    chooseDefaultPurchase();
    renderSellingPlans();
    root.querySelectorAll('[data-option-position]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionPosition) - 1;
      const candidate = [...state.options];
      candidate[optionIndex] = button.dataset.optionValue;
      const possible = config.variants.some((variant) => variant.available && variant.options.every((value, index) => index === optionIndex || value === candidate[index]) && variant.options[optionIndex] === candidate[optionIndex]);
      button.disabled = !possible;
      button.setAttribute('aria-disabled', String(!possible));
      const selected = state.options[optionIndex] === button.dataset.optionValue;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    root.querySelectorAll('[data-option-selection]').forEach((node) => { node.textContent = `${state.options[Number(node.dataset.optionSelection) - 1]} selected`; });
    root.querySelector('[data-summary-variant]').textContent = state.variant.title;
    const oneTimePrice = root.querySelector('[data-one-time-price]');
    if (oneTimePrice) oneTimePrice.textContent = formatMoney(state.variant.price);
    root.querySelector('[data-stock-status]').textContent = state.variant.available ? 'In stock' : 'Sold out';
    addButton.disabled = !state.variant.available;
    root.querySelector('[data-add-label]').textContent = state.variant.available ? 'Add to cart' : 'Sold out';
    setGalleryMedia(state.variant.featuredMediaId);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', state.variant.id);
    window.history.replaceState({}, '', url);
    updatePurchaseUI();
  }

  root.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => setGalleryMedia(button.dataset.mediaTarget)));
  root.querySelectorAll('[data-option-position]').forEach((button) => button.addEventListener('click', () => {
    if (button.disabled) return;
    state.options[Number(button.dataset.optionPosition) - 1] = button.dataset.optionValue;
    updateVariant();
  }));
  root.querySelector('[data-purchase-option="one-time"]')?.addEventListener('change', () => {
    state.purchaseGroup = 'one-time';
    state.sellingPlanId = null;
    updatePurchaseUI();
  });
  root.querySelectorAll('[data-quantity-change]').forEach((button) => button.addEventListener('click', () => {
    quantityInput.value = Math.max(1, (Number(quantityInput.value) || 1) + Number(button.dataset.quantityChange));
    updatePurchaseUI();
  }));
  quantityInput.addEventListener('change', () => {
    quantityInput.value = Math.max(1, Number(quantityInput.value) || 1);
    updatePurchaseUI();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.variant.available) return;
    addButton.disabled = true;
    root.querySelector('[data-add-label]').textContent = 'Adding...';
    try {
      const response = await fetch(window.SaltySailor.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This coffee could not be added to the cart.');
      document.dispatchEvent(new CustomEvent('salty:cart-added', { detail: { title: config.productTitle, item: result } }));
    } catch (error) {
      const status = document.querySelector('[data-cart-status]');
      if (status) status.textContent = error.message;
    } finally {
      addButton.disabled = !state.variant.available;
      root.querySelector('[data-add-label]').textContent = state.variant.available ? 'Add to cart' : 'Sold out';
    }
  });

  const recommendations = document.querySelector('[data-product-recommendations]');
  if (recommendations?.dataset.url) {
    fetch(recommendations.dataset.url).then((response) => response.text()).then((text) => {
      const html = new DOMParser().parseFromString(text, 'text/html');
      const replacement = html.querySelector('[data-product-recommendations]');
      if (replacement?.querySelector('.product-card')) recommendations.replaceWith(replacement);
    }).catch(() => {});
  }

  state.variant = getVariant();
  chooseDefaultPurchase();
  renderSellingPlans();
  updateVariant();
})();
